import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { fetchDomains } from '../api/domains.api';
import styles from './DominiosCatalog.module.css';

export default function DominiosCatalog() {
  const [searchTerm, setSearchTerm] = useState('');
  const [domainsData, setDomainsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const loadDomains = async () => {
      try {
        const data = await fetchDomains();
        setDomainsData(data);
      } catch (err) {
        console.error('Error al cargar dominios:', err);
        setError('No se pudieron cargar los dominios. Intenta de nuevo más tarde.');
      } finally {
        setLoading(false);
      }
    };
    loadDomains();
  }, []);

  const filteredDomains = domainsData.filter(domain => 
    domain.domain.toLowerCase().includes(searchTerm.toLowerCase()) || 
    domain.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className={styles.catalogContainer}>
      <Helmet>
        <title>Venta de Dominios Premium | Rolsys Solutions</title>
        <meta name="description" content="Adquiere dominios premium exclusivos directamente con nosotros. Dominios ideales para eventos, TIC, tecnología y más." />
      </Helmet>

      <div className={styles.heroSection}>
        <motion.h1 
          className={styles.title}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          Encuentra el Dominio Perfecto
        </motion.h1>
        <motion.p 
          className={styles.subtitle}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          Adquiere dominios premium exclusivos de los cuales somos registradores únicos y propietarios directos. Compra segura y sin intermediarios.
        </motion.p>
        
        <motion.div 
          className={styles.searchWrapper}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <input 
            type="text" 
            className={styles.searchInput}
            placeholder="Ej. midominio.com o Categoría..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </motion.div>
      </div>

      <div className={styles.resultsContainer}>
        {loading ? (
          <div className={styles.noResults}>Cargando dominios...</div>
        ) : error ? (
          <div className={styles.noResults}>{error}</div>
        ) : filteredDomains.length === 0 ? (
          <div className={styles.noResults}>
            No encontramos dominios que coincidan con tu búsqueda.
          </div>
        ) : (
          <motion.div 
            className={styles.grid}
            initial="hidden"
            animate="visible"
            variants={{
              hidden: { opacity: 0 },
              visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
            }}
          >
            {filteredDomains.map((domain, index) => (
              <motion.div 
                key={index} 
                className={styles.card}
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  visible: { opacity: 1, y: 0 }
                }}
              >
                <div className={styles.cardHeader}>
                  <span className={`${styles.badge} ${styles.badgeCategory}`}>{domain.category || 'Premium'}</span>
                  <span className={`${styles.badge} ${styles.badgeStatus}`}>Disponible</span>
                </div>
                
                <h3 className={styles.domainName}>{domain.domain}</h3>
                <p className={styles.domainDesc}>{domain.description}</p>
                
                <div className={styles.cardFooter}>
                  <button 
                    className={styles.actionButton}
                    onClick={() => navigate(`/dominios/${domain.domain}`)}
                  >
                    Más información
                  </button>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>
    </div>
  );
}
